import { sortBy } from "@rauricoste/core";
import { EventSync, EventSyncStatus } from "./EventSync";
import { TransportEvent, TransportEventSent } from "./TransportEvent";

export type OptimisticState<State> = {
  state: State;
  optimisticEventIds: string[];
};

export type OptimisticEvent<Event> = {
  event: Event;
  eventId: string;
  type: "send" | "receive";
};

export type OptimisticEventSyncStatus =
  | EventSyncStatus
  | "optimisticallySynced";

export class OptimisticEventSync<Event> {
  private optimisticEvents: TransportEventSent<Event>[];
  private wrappedEventSync: EventSync<Event>;

  constructor(
    public readonly eventSync: EventSync<Event>,
    public readonly onUndoEvent?: (event: Event) => void
  ) {
    this.optimisticEvents = [];
    this.wrappedEventSync = new EventSync<Event>(
      {
        getNewEventsFrom: async (ts: number) => {
          const events = await eventSync.syncInterface.getNewEventsFrom(ts);
          const sortedEvents = sortBy(
            events,
            (event) => `${event.timestamp}-${event.eventId}`
          );
          sortedEvents.forEach((event) => this.applyReceiveEvent(event));
          return events;
        },
        sendEvents: async (events: TransportEventSent<Event>[]) => {
          await eventSync.syncInterface.sendEvents(events);
        },
        delete: async () => {
          this.optimisticEvents = [];
          await eventSync.syncInterface.delete();
        },
      },
      undefined,
      eventSync.lastTimestamp
    );
  }
  private applySendEvent(event: TransportEventSent<Event>) {
    const { optimisticEvents } = this;
    optimisticEvents.push(event);
    this.eventSync.onEvent?.(event.payload);
  }
  private applyReceiveEvent(event: TransportEvent<Event>) {
    const { optimisticEvents, onUndoEvent } = this;
    const index = optimisticEvents.findIndex(
      (eventIte) => event.eventId === eventIte.eventId
    );
    if (index !== -1) {
      optimisticEvents.splice(index, 1);
    } else {
      if (optimisticEvents.length) {
        // conflict
        if (onUndoEvent) {
          [...optimisticEvents]
            .reverse()
            .forEach((optimisticEvent) => onUndoEvent(optimisticEvent.payload));
          this.eventSync.onEvent?.(event.payload);
          optimisticEvents.forEach((optimisticEvent) =>
            this.eventSync.onEvent?.(optimisticEvent.payload)
          );
        } else {
          throw new Error(
            `conflict: event=${JSON.stringify(
              event
            )}, optimisticEvents=${JSON.stringify(optimisticEvents)}`
          );
        }
      } else {
        this.eventSync.onEvent?.(event.payload);
      }
    }
  }

  async getNewEvents(): Promise<void> {
    return this.wrappedEventSync.getNewEvents();
  }

  send(event: Event) {
    const { transportEvent } = this.wrappedEventSync.send(event);
    this.applySendEvent(transportEvent);
  }

  getSyncStatus(): OptimisticEventSyncStatus {
    const status = this.wrappedEventSync.getSyncStatus();
    if (status === "synced" && this.optimisticEvents.length) {
      return "optimisticallySynced";
    }
    return status;
  }
  get isUpToDate(): boolean {
    return this.wrappedEventSync.isUpToDate;
  }
}
